// Visible, human-readable explanation of why an org's subscription is
// blocking (suspended) or about to block (past_due). Subscription status is
// deliberately independent of organization.status — nothing else in the UI
// tells the customer that new check-ins are blocked for non-payment, so this
// notice is the surface for it. Shared by the app-shell banner and the
// Subscription page so both always say the same thing.

import { useTranslation } from '@jq4race/i18n';
import type { SubscriptionResponse } from '@jq4race/shared';
import { Link } from '@tanstack/react-router';
import { TriangleAlert } from 'lucide-react';

export type NoticeStatus = Extract<SubscriptionResponse['status'], 'suspended' | 'past_due'>;

export function isNoticeStatus(status: SubscriptionResponse['status']): status is NoticeStatus {
  return status === 'suspended' || status === 'past_due';
}

interface SubscriptionStatusNoticeProps {
  status: NoticeStatus;
  /** Dunning deadline; the manual past_due copy names the date only when it is known. */
  graceUntil?: string | null;
  /**
   * Decides the past_due copy: manual billing has no card, the debt is overdue
   * invoices; automatic (card) billing keeps the card-fix copy.
   */
  collectionMethod?: SubscriptionResponse['collectionMethod'];
  /** Adds the "view subscription" link (omit on the Subscription page itself). */
  showLink?: boolean;
}

function formatLongDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function SubscriptionStatusNotice({
  status,
  graceUntil,
  collectionMethod,
  showLink = false,
}: SubscriptionStatusNoticeProps) {
  const { t, i18n } = useTranslation('subscription');

  const message =
    status === 'suspended'
      ? t('statusNotice.suspended')
      : collectionMethod !== 'manual'
        ? t('paymentFailedBanner')
        : graceUntil
          ? t('statusNotice.pastDue', { date: formatLongDate(graceUntil, i18n.language) })
          : t('statusNotice.pastDueNoDate');

  const palette =
    status === 'suspended'
      ? 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger'
      : 'border-jq-warning/40 bg-jq-warning/10 text-jq-charcoal';

  return (
    <div
      role="alert"
      data-testid="subscription-status-notice"
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      {showLink && (
        <Link
          to="/dashboard/settings/subscription"
          className="shrink-0 font-medium underline underline-offset-2"
        >
          {t('statusNotice.cta')}
        </Link>
      )}
    </div>
  );
}
